import { application } from "../../../../_runtime/bootstrap";
import { FC, useEffect, useState } from "react";
import { PresetsTableSelection } from "./PresetsTableSelection";
import { Modal } from "taltech-styleguide";
import { useModal } from "@components-common/modals";
import { ActionButton } from "../../../../components-common/buttons/ActionButton";
import { useNotifications } from "@hooks";

type Props = {
  props: {
    id: string;
    getSource: string;
    createSource: string;
    selectedIdField: string;
    columns: {
      header: string;
      accessor: string;
    }[];
  };
};

/**
 * Functional Component: ModalContentPrests
 * 
 * This component renders the content for a modal that allows the user to add items to a preset.
 * It includes a table for selecting items and buttons for adding the selected items.
 * 
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.id - The ID of the preset.
 * @param {string} props.getSource - The source URL for fetching items to be added.
 * @param {string} props.createSource - The source URL for creating new preset items.
 * @param {string} props.selectedIdField - The field name for the selected item ID.
 * @param {Array} props.columns - The columns configuration for the table.
 * 
 * Returns:
 * @returns {JSX.Element} A JSX element containing the modal content for adding items to a preset.
 */
export const ModalContentPrests: FC<Props> = ({ props }) => {
  const userNotifications = useNotifications();
  const { hideModal } = useModal();
  const { id, getSource, createSource, selectedIdField, columns } = props;
  const [selected, setSelected] = useState<string[]>([]);
  const [tableItems, setTableItems] = useState<any[]>([]);

  useEffect(() => {
    application
      .getHttpClient()
      .get(getSource)
      .then((res: any) => {
        setTableItems(res.body.items);
      });
  }, []);

  const createItems = () => {
    if (selected.length === 0) {
      userNotifications.error({ title: "Please select at least one item" });
      return;
    }

    selected.map((itemId, index) => {
      application
        .getHttpClient()
        .post(createSource, {
          presetId: id,
          [selectedIdField]: itemId,
        })
        .then(() => {
          if (index === selected.length - 1) {
            const refetchTableEvent = new CustomEvent("refetchTable");
            document.dispatchEvent(refetchTableEvent);
            hideModal();
          }
        });
    });
  };

  return (
    <>
      <Modal.Header>Add items</Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-3">
          {tableItems.length === 0 && <div>No items found.</div>}
          {tableItems.length > 0 && (
            <div style={{ maxWidth: "100%", overflow: "auto" }}>
              <PresetsTableSelection
                tableItems={tableItems}
                columns={columns}
                selected={selected}
                setSelected={setSelected}
                hasEdit={true}
              />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-end">
        <ActionButton kind="close" onAction={() => hideModal()} />
        <ActionButton kind="add" onAction={() => createItems()} />
      </Modal.Footer>
    </>
  );
};
